"use client";

import { useQuery } from "@tanstack/react-query";

import { RunsService, type RunNoIo } from "@/generated";
import { pickBindingOutputValue } from "./useBindingResolvedValues";

const IO_READY_RUN_STATUSES = new Set<RunNoIo["status"]>([
  "completed",
  "failed",
  "stopped",
  "awaiting_human_feedback",
]);

const RUNS_PAGE_SIZE = 25;
const MAX_IO_LOOKUPS = 5;

export interface LastRunVariableValue {
  value: unknown;
  runId: string;
  runTimestamp: string;
}

export interface VariableRefSource {
  sourceNodeId: string;
  outputName: string;
}

export function parseVariableRefSource(
  ref: string | null | undefined
): VariableRefSource | null {
  if (!ref) return null;
  const dotIdx = ref.indexOf(".");
  if (dotIdx <= 0) return null;
  const sourceNodeId = ref.slice(0, dotIdx);
  const outputName = ref.slice(dotIdx + 1);
  if (!outputName || sourceNodeId === "__secrets__") return null;
  return { sourceNodeId, outputName };
}

function runMayContainNode(run: RunNoIo, sourceNodeId: string): boolean {
  if (!IO_READY_RUN_STATUSES.has(run.status)) return false;
  if (run.single_node_id) return run.single_node_id === sourceNodeId;
  // Partial runs record the executed subset in node_ids; null/empty means the
  // whole definition ran (whether this node existed then is settled by IO).
  if (Array.isArray(run.node_ids) && run.node_ids.length > 0) {
    return run.node_ids.includes(sourceNodeId);
  }
  return true;
}

async function findLastRunValue(
  groupId: string,
  workflowId: string,
  sourceNodeId: string,
  outputName: string
): Promise<LastRunVariableValue | null> {
  const res = await RunsService.getWorkflowRuns({
    path: { group_id: groupId, workflow_id: workflowId },
    query: { order_by: "created_at", sort: "desc", size: RUNS_PAGE_SIZE },
  });

  const candidates = (res.data?.items ?? []).filter((run) =>
    runMayContainNode(run, sourceNodeId)
  );

  for (const run of candidates.slice(0, MAX_IO_LOOKUPS)) {
    try {
      const io = await RunsService.getRunIo({
        path: {
          group_id: groupId,
          run_id: run.id,
          node_id: sourceNodeId,
          it: 1,
        },
      });
      const value = pickBindingOutputValue(io.data?.outputs, outputName);
      if (value !== undefined) {
        return {
          value,
          runId: run.id,
          runTimestamp: run.finished_at ?? run.created_at,
        };
      }
    } catch {
      // Expired/archived IO for one run just means "keep walking back".
    }
  }
  return null;
}

export function useLastRunVariableValue({
  groupId,
  workflowId,
  sourceNodeId,
  outputName,
  enabled = true,
}: {
  groupId: string | undefined;
  workflowId: string | undefined;
  sourceNodeId: string;
  outputName: string;
  enabled?: boolean;
}): { data: LastRunVariableValue | null | undefined; isLoading: boolean } {
  const query = useQuery({
    queryKey: [
      "v2-last-run-variable-value",
      groupId,
      workflowId,
      sourceNodeId,
      outputName,
    ],
    queryFn: () =>
      findLastRunValue(
        groupId ?? "",
        workflowId ?? "",
        sourceNodeId,
        outputName
      ),
    enabled: enabled && !!groupId && !!workflowId,
    staleTime: 15_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return { data: query.data, isLoading: query.isLoading };
}
